import React, { useState, useEffect } from "react";
import classNames from "classnames/bind";
import styles from "./ProfilePage.module.scss";
import { ArrowLeft } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import useAuthStore from "../../stores/Auth/AuthStore";
import useTranslations from "../../hooks/useTranslations";

// Components
import Sidebar from "./components/Sidebar";
import ProfileForm from "./components/ProfileForm";
import OrdersSection from "./components/OrdersSection";

// Hooks
import { useProfileForm } from "./hooks/useProfileForm";
import { useOrderHistory } from "./hooks/useOrderHistory";

// Utils
import {
  getStatusColor,
  getStatusText,
  getPaymentStatusText,
  getPaymentMethodText,
} from "./utils/statusUtils";

const cx = classNames.bind(styles);

const ProfilePage = () => {
  // Authentication
  const { user, isAuthenticated, logout } = useAuthStore();
  
  // Translations
  const currentTranslations = useTranslations();
  
  // Location and query params
  const location = useLocation();
  
  // Local state
  const [activeTab, setActiveTab] = useState("profile");

  // Custom hooks
  const {
    formData,
    isEditing,
    isSaving,
    handleInputChange,
    handleEdit,
    handleSave,
    handleCancel,
  } = useProfileForm();

  const {
    orderHistory,
    isLoading,
    currentPage,
    expandedShipping,
    expandedItems,
    toggleShippingInfo,
    toggleOrderItems,
    handlePageChange,
  } = useOrderHistory();

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      window.location.href = "/";
    }
  }, [isAuthenticated]);

  // Handle query parameters for tab navigation
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam === 'orders') {
      setActiveTab('orders');
    }
  }, [location.search]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Đăng xuất thành công!");
      window.location.href = "/";
    } catch (error) {
      toast.error("Có lỗi xảy ra khi đăng xuất!");
    }
  };
  
  // Utility functions with translations
  const getStatusColorWithTranslations = (status) => getStatusColor(status);
  const getStatusTextWithTranslations = (status) => getStatusText(status, currentTranslations);
  const getPaymentStatusTextWithTranslations = (status) => getPaymentStatusText(status, currentTranslations);
  const getPaymentMethodTextWithTranslations = (method) => getPaymentMethodText(method, currentTranslations);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className={cx("wrapper")}>
      <div className={cx("header")}>
        <Link to="/" className={cx("back-link")}>
          <ArrowLeft size={20} />
          {currentTranslations?.Profile?.back_to_home}
        </Link>
      </div>

      <div className={cx("content")}>
        {/* Sidebar */}
        <Sidebar
          user={user}
          formData={formData}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          handleLogout={handleLogout}
          currentTranslations={currentTranslations}
        />

        {/* Main Content */}
        <div className={cx("main-content")}>
          {activeTab === "profile" && (
            <ProfileForm
              formData={formData}
              isEditing={isEditing}
              isSaving={isSaving}
              currentTranslations={currentTranslations}
              handleInputChange={handleInputChange}
              handleEdit={handleEdit}
              handleSave={handleSave}
              handleCancel={handleCancel}
            />
          )}

          {activeTab === "orders" && (
            <OrdersSection
              orderHistory={orderHistory}
              isLoading={isLoading}
              currentPage={currentPage}
              currentTranslations={currentTranslations}
              expandedShipping={expandedShipping}
              expandedItems={expandedItems}
              onPageChange={handlePageChange}
              toggleShippingInfo={toggleShippingInfo}
              toggleOrderItems={toggleOrderItems}
              getStatusColor={getStatusColorWithTranslations}
              getStatusText={getStatusTextWithTranslations}
              getPaymentStatusText={getPaymentStatusTextWithTranslations}
              getPaymentMethodText={getPaymentMethodTextWithTranslations}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
